import { memo, useState, useCallback } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";

import { MerEditCardProps } from "./types";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { MER_EDIT_FORM_SCHEMA } from "./validation";
import { validateYup } from "services/forms/validation/validation";

const MerEditCard: React.FC<MerEditCardProps> = (props) => {
  const { settings, setSettings, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});

  const environmentOptions = [
    { value: "sandbox", text: t("admin.integration.mer.environment.sandbox") },
    { value: "production", text: t("admin.integration.mer.environment.production") },
  ];

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      settings,
      MER_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Add API call to save settings
      if (onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setSettings,
    settings
  );

  const handleEnvironmentChange = useCallback(
    (value: string | number | null) => {
      setSettings((prev) => ({
        ...prev,
        environment: value as "production" | "sandbox",
      }));
    },
    [setSettings]
  );

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="flex gap-3 h-full">
            <form noValidate className="text-left flex-1 " onSubmit={handleFormSubmit}>
              <div className="p-4">
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="username"
                    label="Korisničko ime"
                    onChange={handleTextFieldChange}
                    value={settings.username}
                    error={errors.username}
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="password"
                    name="password"
                    label="Lozinka"
                    onChange={handleTextFieldChange}
                    value={settings.password}
                    error={errors.password}
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="companyId"
                    label="OIB tvrtke (11 znamenki)"
                    onChange={handleTextFieldChange}
                    value={settings.companyId}
                    error={errors.companyId}
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="companyBu"
                    label="Poslovni prostor"
                    onChange={handleTextFieldChange}
                    value={settings.companyBu}
                    error={errors.companyBu}
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="softwareId"
                    label="Software ID"
                    onChange={handleTextFieldChange}
                    value={settings.softwareId}
                    error={errors.softwareId}
                  />
                </div>
                <div className="mb-3">
                  <Dropdown
                    name="environment"
                    label={t("admin.integration.mer.environment")}
                    options={environmentOptions}
                    onChange={handleEnvironmentChange}
                    value={settings.environment}
                    error={errors.environment}
                  />
                </div>
              </div>
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={() => onClose()}
                />
                <Button
                  variant="primary"
                  children={t("admin.save")}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(MerEditCard);
